import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Building, Download, Users, Eye, Upload, CheckSquare, Square } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import BulkImportPreview from "@/components/BulkImportPreview";
import { ContactsTable } from "../components/contacts/ContactsTable";
import { CallHistory, LastCallCell, LogCallButton, QuickLogCallButton, withLastCall, isStaleCall } from "../components/calls/CallHistory";

export default function Leads({ user }) {
  const isSalesTeamMember = user?.department === "Sales" && user?.access_level === "Team Member";
  const isSalesTeamLead = user?.department === "Sales" && user?.access_level === "Team Lead";
  const isSalesManager = user?.department === "Sales" && user?.access_level === "Manager";
  const isSalesAdmin = user?.department === "Sales" && user?.access_level === "Admin";
  // Sales users who can delete contacts: only Sales Admin and non-Sales departments
  const canDeleteContacts = !user?.department || user?.department !== "Sales" || isSalesAdmin;
  
  const [leads, setLeads] = useState([]);
  const [showTeamData, setShowTeamData] = useState(false); // Toggle state for Team Leads
  const [teamMembers, setTeamMembers] = useState([]); // List of team members for filter
  const [selectedTeamMember, setSelectedTeamMember] = useState("all"); // Selected team member filter
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  
  // Contact management states
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [editContactMode, setEditContactMode] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  
  // View client details state
  const [viewLeadDialogOpen, setViewLeadDialogOpen] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    lead_name: "",
    time_zone: "",
    state: "",
    country: "",
    show_my_leads: false  // Filter for sales users
  });
  
  // Sales assignment states
  const [leadSalesUsers, setLeadSalesUsers] = useState({});  // Map of lead_id -> sales user info
  const [users, setUsers] = useState([]);  // All users for assignment dropdown
  const [salesAssignmentDialogOpen, setSalesAssignmentDialogOpen] = useState(false);
  const [salesAssignmentLead, setSalesAssignmentLead] = useState(null);
  const [salesAssignmentHistory, setSalesAssignmentHistory] = useState([]);
  const [assignmentFormData, setAssignmentFormData] = useState({
    sales_user_id: "",
    start_date: "",
    notes: ""
  });

  // Bulk selection and assignment states
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkSalesAssignmentDialogOpen, setBulkSalesAssignmentDialogOpen] = useState(false);
  const [bulkAssignmentFormData, setBulkAssignmentFormData] = useState({
    sales_user_id: "",
    start_date: "",
    notes: ""
  });
  
  // Bulk upload with preview states
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  
  // Bulk upload error dialog states
  const [uploadErrorsDialogOpen, setUploadErrorsDialogOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccessCount, setUploadSuccessCount] = useState(0);
  
  // Lead approval states
  const [leadApprovals, setLeadApprovals] = useState({}); // Map of lead_id -> approval status
  
  // Team member IDs cache for team leads
  const [teamMemberIds, setTeamMemberIds] = useState([]);
  const [teamDataLoaded, setTeamDataLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    lead_number: "",
    lead_name: "",
    lead_status: "Recording",
    industry: "",
    time_zone: "EST",
    phone: "",
    fax: "",
    website: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: ""
  });

  const [contactFormData, setContactFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",  // Legacy field
    business_phone: "",
    business_extension: "",
    mobile_phone: "",
    email: "",
    designation: "",
    roles: [],
    special_notes: ""
  });

  const softwareOptions = ["SDS/2", "Tekla", "Advance Steel", "AutoCAD", "SolidWorks"];
  const roleOptions = ["Project", "Billing", "Management", "FDO", "Estimator", "Owner"];

  // Handle country change
  const handleCountryChange = (country) => {
    setFormData({ 
      ...formData, 
      country: country, 
      state: ""
    });
  };

  // Format phone number to XXX-XXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedNumber = phoneNumber.slice(0, 10);
    
    // Format as XXX-XXX-XXXX
    if (limitedNumber.length <= 3) {
      return limitedNumber;
    } else if (limitedNumber.length <= 6) {
      return `${limitedNumber.slice(0, 3)}-${limitedNumber.slice(3)}`;
    } else {
      return `${limitedNumber.slice(0, 3)}-${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
    }
  };

  // Handle phone input with validation and formatting
  const handlePhoneInput = (field, value) => {
    // Only allow digits and hyphens, remove any alphabets
    const cleanValue = value.replace(/[^0-9-]/g, '');
    const formattedNumber = formatPhoneNumber(cleanValue);
    setContactFormData({ ...contactFormData, [field]: formattedNumber });
  };
  
  // Countries list with USA and Canada at top
  const countries = [
    "United States",
    "Canada",
    "---", // Separator
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
    "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
    "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
    "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Cape Verde", "Central African Republic",
    "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus",
    "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador",
    "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Fiji", "Finland",
    "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala",
    "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia",
    "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya",
    "Kiribati", "North Korea", "South Korea", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
    "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macedonia", "Madagascar",
    "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
    "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique",
    "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
    "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
    "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
    "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
    "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
    "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
    "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo",
    "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
    "United Arab Emirates", "United Kingdom", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
    "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ];
  
  // US States
  const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri",
    "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
    "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming"
  ];
  
  // Canadian Provinces
  const canadianProvinces = [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
    "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island",
    "Quebec", "Saskatchewan", "Yukon"
  ];
  
  // Get states based on selected country
  const getStatesForCountry = (country) => {
    if (country === "United States") return usStates;
    if (country === "Canada") return canadianProvinces;
    return [];
  };

  useEffect(() => {
    fetchLeads();
    fetchUsers();
    fetchTeamMemberIds(); // Fetch team member IDs for team leads
    if (isSalesTeamLead || isSalesManager) {
      fetchTeamMembersForFilter(); // Fetch team members for filter dropdown
    }
  }, []);
  
  // Debug: Log when toggle changes
  useEffect(() => {
    if (isSalesTeamLead) {
      console.log("Leads - Toggle changed - showTeamData:", showTeamData);
      console.log("Leads - Team member IDs:", teamMemberIds);
      console.log("Leads - Current user ID:", user?.id);
    }
  }, [showTeamData, teamMemberIds]);

  // Fetch team members for filter dropdown (for Team Leads and Managers)
  const fetchTeamMembersForFilter = async () => {
    try {
      const response = await api.get("/teams/my-team-members-details");
      const memberDetails = response.data.team_members || [];
      setTeamMembers(memberDetails);
    } catch (error) {
      console.error("Failed to fetch team members for filter:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get("/leads");
      console.log("Fetched leads:", response.data);
      setLeads(response.data);
      // Fetch current sales users for all leads
      await fetchAllLeadSalesUsers();
      // Fetch all approvals to check lead status
      await fetchAllLeadApprovals(response.data);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLeadSalesUsers = async () => {
    try {
      const response = await api.get("/leads/current-sales-users");
      setLeadSalesUsers(response.data || {});
    } catch (error) {
      console.error("Error fetching sales assignments:", error);
    }
  };

  const handleApplyImport = async (applyData) => {
    try {
      const response = await api.post("/import/leads/apply", applyData);
      
      toast.success(`Successfully imported ${response.data.imported} and updated ${response.data.updated} leads!`);
      
      if (response.data.errors.length > 0) {
        setUploadErrors(response.data.errors);
        setUploadSuccessCount(response.data.imported + response.data.updated);
        setUploadErrorsDialogOpen(true);
      }
      
      setImportPreviewOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to apply import");
    }
  };

  const fetchAllLeadApprovals = async (leadsList) => {
    try {
      // Fetch all approvals (not just for specific user access level)
      const response = await api.get("/lead-approvals");
      const approvalsMap = {};
      
      // Create a map of lead_id -> approval for quick lookup
      response.data.forEach(approval => {
        // Only track non-rejected approvals
        if (!["rejected", "unapproved"].includes(approval.approval_status)) {
          approvalsMap[approval.lead_id] = approval;
        }
      });
      
      setLeadApprovals(approvalsMap);
    } catch (error) {
      // If user doesn't have permission, that's okay - they won't see approval features
      console.log("Could not fetch approvals (permission issue or no approvals exist)");
    }
  };

  const checkLeadApprovalStatus = async (leadId) => {
    try {
      // Check if this lead has an approval
      const response = await api.get("/lead-approvals");
      const approval = response.data.find(a => a.lead_id === leadId && !["rejected", "unapproved"].includes(a.approval_status));
      if (approval) {
        setLeadApprovals(prev => ({
          ...prev,
          [leadId]: approval
        }));
      }
    } catch (error) {
      console.error("Error checking approval status:", error);
    }
  };

  const handleRequestApproval = async (lead) => {
    try {
      const response = await api.post(`/leads/${lead.id}/approval`);
      toast.success("Lead approval request created successfully");
      setLeadApprovals(prev => ({
        ...prev,
        [lead.id]: response.data
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request approval");
    }
  };

  // Fetch team member user IDs from backend (for any user - backend determines if they're a team lead)
  const fetchTeamMemberIds = async () => {
    if (!user) {
      setTeamMemberIds([]);
      setTeamDataLoaded(true);
      return;
    }
    
    try {
      console.log("Leads - Fetching team members for user:", user.username);
      const response = await api.get("/teams/my-team-members");
      const ids = response.data.user_ids || [];
      setTeamMemberIds(ids);
      setTeamDataLoaded(true);
      console.log("Leads - Team member IDs fetched:", ids);
      
      // If we got more than just current user's ID, user is a team lead
      if (ids.length > 1) {
        console.log(`✓ User is a Team Lead with ${ids.length} team members`);
      } else {
        console.log(`User is not a team lead (only 1 ID returned)`);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      // Fallback to just current user
      setTeamMemberIds([user.id]);
      setTeamDataLoaded(true);
    }
  };

  // Get team member user IDs for filtering (Sales Team Lead only)
  const getTeamMemberUserIds = () => {
    // Return cached team member IDs, or fallback to current user
    return teamMemberIds.length > 0 ? teamMemberIds : [user?.id];
  };

  const fetchUsers = async () => {
    // Sales users don't have permission to view users list
    if (user?.department === "Sales") {
      console.log("Skipping user fetch for Sales user");
      return;
    }
    
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      // Only show error if not a Sales user
      if (user?.department !== "Sales") {
        console.log("Failed to fetch users - may not have permission");
      }
    }
  };

  const fetchContactsForLead = async (leadId) => {
    try {
      const response = await api.get(`/contacts?lead_id=${leadId}`);
      setContacts(response.data);
    } catch (error) {
      toast.error("Failed to fetch contacts");
    }
  };

  const handleViewContacts = (client) => {
    setSelectedLead(client);
    setContactsDialogOpen(true);
    fetchContactsForLead(client.id);
  };

  const handleAddContact = () => {
    resetContactForm();
    setEditContactMode(false);
    setContactFormOpen(true);
  };

  const handleEditContact = (contact) => {
    setCurrentContact(contact);
    setContactFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      phone: contact.phone || "",
      business_phone: contact.business_phone || "",
      business_extension: contact.business_extension || "",
      mobile_phone: contact.mobile_phone || "",
      email: contact.email || "",
      designation: contact.designation || "",
      roles: contact.roles || [],
      special_notes: contact.special_notes || ""
    });
    setEditContactMode(true);
    setContactFormOpen(true);
  };

  const handleContactRoleToggle = (role) => {
    setContactFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...contactFormData,
        lead_id: selectedLead.id
      };

      if (editContactMode) {
        await api.put(`/contacts/${currentContact.id}`, contactFormData);
        toast.success("Contact updated successfully!");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact added successfully!");
      }
      
      setContactFormOpen(false);
      fetchContactsForLead(selectedLead.id);
      resetContactForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editContactMode ? 'update' : 'add'} contact`);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success("Contact deleted successfully");
      fetchContactsForLead(selectedLead.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contact");
    }
  };

  const resetContactForm = () => {
    setContactFormData({
      first_name: "",
      last_name: "",
      phone: "",
      business_phone: "",
      business_extension: "",
      mobile_phone: "",
      email: "",
      designation: "",
      roles: [],
      special_notes: ""
    });
    setEditContactMode(false);
    setCurrentContact(null);
  };

  const getLatestLeadNumber = () => {
    if (leads.length === 0) return null;
    const validLeads = leads.filter(l => l.lead_number && l.lead_number.startsWith('L'));
    if (validLeads.length === 0) return null;
    
    const sorted = [...validLeads].sort((a, b) => {
      const getNumber = (num) => {
        const match = num.replace('L', '');
        return match ? parseInt(match, 10) : 0;
      };
      return getNumber(b.lead_number) - getNumber(a.lead_number);
    });
    
    return sorted[0].lead_number;
  };

  // Software field removed for Leads module

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData
      };
      
      console.log("Submitting lead payload:", payload);

      if (editMode) {
        const response = await api.put(`/leads/${currentLead.id}`, payload);
        console.log("Update response:", response.data);
        toast.success("Lead updated successfully!");
        setDialogOpen(false);
        fetchLeads();
        resetForm();
      } else {
        // Check for similar leads before creating
        const checkResponse = await api.post("/leads/check-similarity", payload);
        console.log("Similarity check response:", checkResponse.data);
        const { has_name_match, has_address_match, similar_leads } = checkResponse.data;
        
        // Error: Name + Address match (block creation)
        if (has_address_match) {
          const matchedLead = similar_leads.find(c => c.address_match);
          toast.error(
            `Cannot create client: A client with the same name and address already exists (${matchedLead.client_number} - ${matchedLead.client_name}). Please verify the details.`,
            { duration: 6000 }
          );
          return;
        }
        
        // Warning: Name match only (allow with confirmation)
        if (has_name_match) {
          const similarNames = similar_leads.map(c => `${c.client_number} - ${c.client_name}`).join(", ");
          const confirmed = window.confirm(
            `⚠️ Warning: Similar client name(s) found:\n${similarNames}\n\nDo you want to proceed with creating this client?`
          );
          if (!confirmed) {
            return;
          }
        }
        
        // Proceed with creation
        const createResponse = await api.post("/leads", payload);
        console.log("Create response:", createResponse.data);
        toast.success("Lead created successfully!");
        setDialogOpen(false);
        fetchLeads();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} client`);
    }
  };

  const handleEdit = (lead) => {
    setCurrentLead(lead);
    
    setFormData({
      lead_number: lead.lead_number || "",
      lead_name: lead.lead_name || "",
      lead_status: lead.lead_status || "Recording",
      industry: lead.industry || "",
      time_zone: lead.time_zone || "EST",
      phone: lead.phone || "",
      fax: lead.fax || "",
      website: lead.website || "",
      address_line1: lead.address_line1 || "",
      address_line2: lead.address_line2 || "",
      city: lead.city || "",
      state: lead.state || "",
      zip_code: lead.zip_code || "",
      country: lead.country || ""
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleViewLead = (client) => {
    setViewingLead(client);
    setViewLeadDialogOpen(true);
    // Check if lead has approval
    if (client.lead_status === "RFQ") {
      checkLeadApprovalStatus(client.id);
    }
  };


  // Sales Assignment Functions
  const handleOpenSalesAssignment = async (lead) => {
    setSalesAssignmentLead(lead);
    setSalesAssignmentDialogOpen(true);
    
    // Fetch assignment history for this lead
    try {
      const response = await api.get(`/leads/${lead.id}/sales-assignments`);
      setSalesAssignmentHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch assignment history:", error);
      setSalesAssignmentHistory([]);
    }
    
    // Pre-fill with current assignment if exists
    const currentSales = leadSalesUsers[lead.id];
    setAssignmentFormData({
      sales_user_id: currentSales?.sales_user_id || "",
      start_date: new Date().toISOString().split('T')[0],  // Today's date
      notes: ""
    });
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    
    if (!assignmentFormData.sales_user_id) {
      toast.error("Please select a sales user");
      return;
    }
    if (!assignmentFormData.start_date) {
      toast.error("Please select a start date");
      return;
    }
    
    try {
      const payload = {
        sales_user_id: assignmentFormData.sales_user_id,
        start_date: assignmentFormData.start_date,
        notes: assignmentFormData.notes
      };
      
      await api.post(`/leads/${salesAssignmentLead.id}/sales-assignments`, payload);
      toast.success("Sales user assigned successfully");
      
      // Refresh assignment history to show the new assignment
      const response = await api.get(`/leads/${salesAssignmentLead.id}/sales-assignments`);
      setSalesAssignmentHistory(response.data);
      
      // Refresh lead list to update sales user display
      fetchLeads();
      
      // Reset form but keep dialog open to show updated history
      setAssignmentFormData({
        sales_user_id: "",
        start_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
    } catch (error) {
      console.error("Sales assignment error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to assign sales user");
    }
  };


  // Bulk Sales Assignment Functions
  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSelectAllLeads = (checked) => {
    if (checked) {
      setSelectedLeads(new Set(filteredLeads.map(lead => lead.id)));
    } else {
      setSelectedLeads(new Set());
    }
  };

  const handleOpenBulkSalesAssignment = () => {
    if (selectedLeads.size === 0) {
      toast.error("Please select at least one lead");
      return;
    }
    setBulkSalesAssignmentDialogOpen(true);
  };

  const handleBulkSalesAssignment = async (e) => {
    e.preventDefault();
    
    if (!bulkAssignmentFormData.sales_user_id || !bulkAssignmentFormData.start_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        sales_user_id: bulkAssignmentFormData.sales_user_id,
        start_date: bulkAssignmentFormData.start_date,
        notes: bulkAssignmentFormData.notes || ""
      };
      
      const response = await api.post(`/leads/bulk-sales-assignment`, {
        lead_ids: Array.from(selectedLeads),
        assignment_data: payload
      });
      
      toast.success(response.data.message);
      
      // Refresh leads and clear selection
      fetchLeads();
      setSelectedLeads(new Set());
      setBulkSalesAssignmentDialogOpen(false);
      setBulkAssignmentFormData({
        sales_user_id: "",
        start_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
    } catch (error) {
      console.error("Bulk sales assignment error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to assign sales user to leads");
    }
  };


  const handleEndAssignment = async (assignmentId, leadId) => {
    const endDate = new Date().toISOString().split('T')[0];
    
    try {
      await api.patch(`/leads/${leadId}/sales-assignments/${assignmentId}`, null, {
        params: { end_date: endDate }
      });
      toast.success("Assignment ended successfully");
      
      // Refresh assignment history to show the ended assignment
      const response = await api.get(`/leads/${leadId}/sales-assignments`);
      setSalesAssignmentHistory(response.data);
      
      // Refresh lead list to update sales user display
      fetchLeads();
    } catch (error) {
      console.error("Failed to end assignment:", error);
      toast.error(error.response?.data?.detail || "Failed to end assignment");
    }
  };


  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      await api.delete(`/leads/${clientId}`);
      toast.success("Lead deleted successfully");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const resetForm = () => {
    setFormData({
      lead_number: "",
      lead_name: "",
      lead_status: "Recording",
      industry: "",
      time_zone: "EST",
      phone: "",
      fax: "",
      website: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip_code: "",
      country: ""
    });
    setEditMode(false);
    setCurrentLead(null);
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  // Filter leads based on filter criteria
  const filteredLeads = leads.filter(lead => {
    if (filters.no_call_30 && !isStaleCall(lead.last_call_at)) return false;
    if (filters.lead_name && !lead.lead_name?.toLowerCase().includes(filters.lead_name.toLowerCase())) return false;
    if (filters.time_zone && lead.time_zone !== filters.time_zone) return false;
    if (filters.state && lead.state !== filters.state) return false;
    if (filters.country && lead.country !== filters.country) return false;
    
    // Sales Team Member: show only their assigned leads
    if (isSalesTeamMember) {
      const salesUser = leadSalesUsers[lead.id];
      if (!salesUser || salesUser.sales_user_id !== user.id) return false;
    }
    
    // Sales Team Lead: show only their team's leads
    if (isSalesTeamLead) {
      const salesUser = leadSalesUsers[lead.id];
      if (!salesUser) return false;
      
      // Get all team member IDs including the team lead
      const teamMemberIds = getTeamMemberUserIds();
      // Show only leads assigned to team members (including team lead)
      if (!teamMemberIds.includes(salesUser.sales_user_id)) return false;
      
      // If "My Leads" filter is active, further filter to only team lead's own leads
      if (filters.show_my_leads) {
        if (salesUser.sales_user_id !== user.id) return false;
      }
      
      // If a specific team member is selected in filter
      if (selectedTeamMember && selectedTeamMember !== "all") {
        if (salesUser.sales_user_id !== selectedTeamMember) return false;
      }
    }
    // Sales Manager sees every lead (like Admin/Management); optional team-member filter
    if (isSalesManager && selectedTeamMember && selectedTeamMember !== "all" && leadSalesUsers[lead.id]?.sales_user_id !== selectedTeamMember) return false;

    // "My Leads" filter for non-sales users - show only leads assigned to current user
    if (filters.show_my_leads && !isSalesTeamLead && !isSalesTeamMember) {
      const salesUser = leadSalesUsers[lead.id];
      if (!salesUser || salesUser.sales_user_id !== user.id) return false;
    }
    
    return true;
  });

  const clearFilters = () => {
    setFilters({
      client_name: "",
      time_zone: "",
      state: "",
      country: "",
      show_my_leads: false
    });
    setSelectedTeamMember("all");
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await api.get("/export/leads", {
        responseType: 'blob'
      });
      
      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response type");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads downloaded successfully!");
    } catch (error) {
      console.error("Excel download error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to download leads";
      toast.error(errorMessage);
    }
  };

  const getLeadDisplay = (lead) => {
    const number = lead.lead_number || "N/A";
    const name = lead.lead_name || "Unknown";
    return `${number} - ${name}`;
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="leads-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface mb-2">Leads</h1>
            <p className="text-md3-on-surface-variant">Manage client information and billing details</p>
          </div>
          <div className="flex gap-4 items-center">
            <QuickLogCallButton onLogged={(log) => log.entity_type === "lead" && setLeads((ls) => withLastCall(ls, log))} />
            {/* Create button based on permissions */}
            {user?.permissions?.leads?.create && (
              <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogTrigger asChild>
                  <Button data-testid="create-client-button" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                    <Plus className="mr-2 h-4 w-4" /> New Lead
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? 'Edit Lead' : 'Create New Lead'}</DialogTitle>
                  <DialogDescription className="text-md3-body-medium text-gray-600">
                    {editMode ? 'Update client information' : 'Add a new client with comprehensive details'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Section 1: Basic Information */}
                  <div className="space-y-6">
                    <div className="border-b pb-2">
                      <h3 className="text-md3-title-large">Basic Information</h3>
                    </div>
                    
                    {!editMode && leads.length > 0 && getLatestLeadNumber() && (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-xs text-blue-700">
                          <strong>Last Lead Number:</strong> {getLatestLeadNumber()}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="lead_number" className="text-md3-label-large text-gray-700 font-medium">Lead Number *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="lead_number"
                          placeholder="e.g., L1001 (Manual entry)"
                          value={formData.lead_number}
                          onChange={(e) => setFormData({ ...formData, lead_number: e.target.value })}
                          disabled={editMode}
                          required
                        />
                        <p className="text-xs text-md3-on-surface-variant">Unique identifier (e.g., L1001, L1002)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lead_name" className="text-md3-label-large text-gray-700 font-medium">Lead Name *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="lead_name"
                          placeholder="Company/Organization name"
                          value={formData.lead_name}
                          onChange={(e) => setFormData({ ...formData, lead_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="lead_status" className="text-md3-label-large text-gray-700 font-medium">Lead Status *</Label>
                        <Select value={formData.lead_status} onValueChange={(value) => setFormData({ ...formData, lead_status: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Recording">Recording</SelectItem>
                            <SelectItem value="On Progress">On Progress</SelectItem>
                            <SelectItem value="Qualified">Qualified</SelectItem>
                            <SelectItem value="Prospect">Prospect</SelectItem>
                            <SelectItem value="Future Potential">Future Potential</SelectItem>
                            <SelectItem value="Junk">Junk</SelectItem>
                            <SelectItem value="Inactive">Inactive</SelectItem>
                            <SelectItem value="RFQ">RFQ</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="industry" className="text-md3-label-large text-gray-700 font-medium">Industry</Label>
                        <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Fabricator">Fabricator</SelectItem>
                            <SelectItem value="Erector">Erector</SelectItem>
                            <SelectItem value="Architect">Architect</SelectItem>
                            <SelectItem value="Structural Engineer">Structural Engineer</SelectItem>
                            <SelectItem value="Professional Engineer">Professional Engineer</SelectItem>
                            <SelectItem value="Detailer">Detailer</SelectItem>
                            <SelectItem value="General Contractor">General Contractor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Software and Business Relation Start Date removed for Leads */}
                  </div>

                  {/* Section 2: Contact Information */}
                  <div className="space-y-6">
                    <div className="border-b pb-2">
                      <h3 className="text-md3-title-large">Company Contact Information</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-md3-label-large text-gray-700 font-medium">Phone</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="phone"
                          placeholder="222-333-4444"
                          value={formData.phone}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value.replace(/[^0-9-]/g, ''));
                            setFormData({ ...formData, phone: formatted });
                          }}
                          maxLength={12}
                        />
                        <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="fax" className="text-md3-label-large text-gray-700 font-medium">Fax</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="fax"
                          placeholder="222-333-4444"
                          value={formData.fax}
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value.replace(/[^0-9-]/g, ''));
                            setFormData({ ...formData, fax: formatted });
                          }}
                          maxLength={12}
                        />
                        <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="website" className="text-md3-label-large text-gray-700 font-medium">Website</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="website"
                          type="text"
                          placeholder="www.example.com"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        />
                        <p className="text-xs text-md3-on-surface-variant">Enter website without http:// (e.g., www.example.com)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="time_zone" className="text-md3-label-large text-gray-700 font-medium">Time Zone</Label>
                        <Select value={formData.time_zone} onValueChange={(value) => setFormData({ ...formData, time_zone: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EST">EST - Eastern</SelectItem>
                            <SelectItem value="CST">CST - Central</SelectItem>
                            <SelectItem value="MST">MST - Mountain</SelectItem>
                            <SelectItem value="PST">PST - Pacific</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Address Information (Billing section removed for Leads) */}
                  <div className="space-y-6">
                    <div className="border-b pb-2">
                      <h3 className="text-md3-title-large">Address Information</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-md3-label-large text-gray-700 font-medium">Country</Label>
                        <Select value={formData.country} onValueChange={handleCountryChange}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country, index) => (
                              country === "---" ? (
                                <div key={index} className="border-t my-1"></div>
                              ) : (
                                <SelectItem key={country} value={country}>
                                  {country}
                                </SelectItem>
                              )
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state" className="text-md3-label-large text-gray-700 font-medium">State/Province</Label>
                        {formData.country === "United States" || formData.country === "Canada" ? (
                          <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue placeholder="Select state/province" />
                            </SelectTrigger>
                            <SelectContent>
                              {getStatesForCountry(formData.country).map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="state"
                            placeholder="Enter state/province"
                            value={formData.state}
                            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          />
                        )}
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-md3-body-small font-semibold text-md3-on-surface-variant">Address</h4>
                      <div className="space-y-2">
                        <Label htmlFor="address_line1" className="text-md3-label-large text-gray-700 font-medium">Address Line 1</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="address_line1"
                          placeholder="Street address, P.O. box"
                          value={formData.address_line1}
                          onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_line2" className="text-md3-label-large text-gray-700 font-medium">Address Line 2</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="address_line2"
                          placeholder="Apartment, suite, unit, building, floor, etc."
                          value={formData.address_line2}
                          onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-md3-label-large text-gray-700 font-medium">City</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="city"
                            placeholder="City"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="zip_code" className="text-md3-label-large text-gray-700 font-medium">Zip / Postal Code</Label>
                          <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="zip_code"
                            placeholder="Zip or postal code"
                            value={formData.zip_code}
                            onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-md3-on-surface-variant">Note: Country and State are shared with company information above</p>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                    {editMode ? 'Update Lead' : 'Create Lead'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Contacts Dialog */}
        <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                Contacts for {selectedLead ? getLeadDisplay(selectedLead) : ''}
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Manage contact persons for this lead
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <Button onClick={handleAddContact} size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                <Plus className="mr-2 h-4 w-4" /> Add Contact
              </Button>

              <ContactsTable
                contacts={contacts}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                canDelete={canDeleteContacts}
                onChanged={() => fetchContactsForLead(selectedLead.id)}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Contact Form Dialog */}
        <Dialog open={contactFormOpen} onOpenChange={setContactFormOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editContactMode ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editContactMode ? 'Update contact information' : 'Add a new contact person'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-md3-label-large text-gray-700 font-medium">First Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="first_name"
                    value={contactFormData.first_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-md3-label-large text-gray-700 font-medium">Last Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="last_name"
                    value={contactFormData.last_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="business_phone" className="text-md3-label-large text-gray-700 font-medium">Business Phone</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="business_phone"
                        placeholder="222-333-4444"
                        value={contactFormData.business_phone}
                        onChange={(e) => handlePhoneInput('business_phone', e.target.value)}
                        maxLength={12}
                      />
                      <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                    </div>
                    <div>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="business_extension"
                        placeholder="Ext."
                        value={contactFormData.business_extension}
                        onChange={(e) => setContactFormData({ ...contactFormData, business_extension: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobile_phone" className="text-md3-label-large text-gray-700 font-medium">Mobile Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="mobile_phone"
                    placeholder="222-333-4444"
                    value={contactFormData.mobile_phone}
                    onChange={(e) => handlePhoneInput('mobile_phone', e.target.value)}
                    maxLength={12}
                  />
                  <p className="text-xs text-md3-on-surface-variant mt-1">Format: XXX-XXX-XXXX (numbers only)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="text-md3-label-large text-gray-700 font-medium">Email</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="contact_email"
                    type="email"
                    placeholder="email@example.com"
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="designation" className="text-md3-label-large text-gray-700 font-medium">Designation</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="designation"
                  placeholder="e.g., Project Manager"
                  value={contactFormData.designation}
                  onChange={(e) => setContactFormData({ ...contactFormData, designation: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Role (Multiple possible)</Label>
                <div className="flex gap-4 p-3 border rounded">
                  {roleOptions.map((role) => (
                    <div key={role} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role}`}
                        checked={contactFormData.roles.includes(role)}
                        onCheckedChange={() => handleContactRoleToggle(role)}
                      />
                      <Label htmlFor={`role-${role}`} className="cursor-pointer text-md3-body-small">{role}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="special_notes" className="text-md3-label-large text-gray-700 font-medium">Special Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="special_notes"
                  placeholder="Additional information about this contact..."
                  rows={3}
                  value={contactFormData.special_notes}
                  onChange={(e) => setContactFormData({ ...contactFormData, special_notes: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                {editContactMode ? 'Update Contact' : 'Add Contact'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-md3-title-large text-md3-on-surface ">Leads ({filteredLeads.length})</CardTitle>
              <div className="flex gap-2">
                {/* Hide Bulk Upload and Templates for all Sales users */}
                {user?.department !== "Sales" && (
                  <>
                    {/* Leads Bulk Upload */}
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await api.get("/template/leads", { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'leads_template.xlsx');
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("Leads template downloaded!");
                        } catch (error) {
                          toast.error("Failed to download template");
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Leads Template
                    </Button>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                       onClick={() => document.getElementById('bulk-upload-leads').click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Leads
                    </Button>
                    <input
                      id="bulk-upload-leads"
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          // Call preview endpoint
                          const response = await api.post("/import/leads/preview", formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          setImportPreviewData(response.data);
                          setImportPreviewOpen(true);
                          
                          e.target.value = '';
                        } catch (error) {
                          toast.error("Failed to preview import");
                          e.target.value = '';
                        }
                      }}
                    />
                    
                    {/* Lead Contacts Bulk Upload */}
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await api.get("/template/lead-contacts", { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'lead_contacts_template.xlsx');
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success("Lead Contacts template downloaded!");
                        } catch (error) {
                          toast.error("Failed to download template");
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Lead Contacts Template
                    </Button>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                   onClick={() => document.getElementById('bulk-upload-contacts').click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Contacts
                </Button>
                <input
                  id="bulk-upload-contacts"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      const response = await api.post("/import/contacts", formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      if (response.data.imported > 0) {
                        toast.success(`Successfully imported ${response.data.imported} contacts!`);
                        // Optionally refresh contacts if needed
                      }
                      
                      if (response.data.errors.length > 0) {
                        setUploadErrors(response.data.errors);
                        setUploadSuccessCount(response.data.imported);
                        setUploadErrorsDialogOpen(true);
                      }
                      
                      e.target.value = '';
                    } catch (error) {
                      toast.error("Failed to import contacts");
                      e.target.value = '';
                    }
                  }}
                />
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter Section */}
            <div className="mb-6 p-4 bg-md3-surface rounded-md3-lg border border-md3-outline-variant">
              <h3 className="text-md3-body-small font-semibold mb-3 text-md3-on-surface-variant">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter_lead_name" className="text-xs">Lead Name</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_lead_name"
                    placeholder="Search by name..."
                    value={filters.lead_name}
                    onChange={(e) => setFilters({ ...filters, lead_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_country" className="text-xs">Country</Label>
                  <Select value={filters.country || "all"} onValueChange={(value) => setFilters({ ...filters, country: value === "all" ? "" : value, state: "" })}>
                    <SelectTrigger id="filter_country">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.filter(c => c !== "---").map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_state" className="text-xs">State/Province</Label>
                  {filters.country === "United States" || filters.country === "Canada" ? (
                    <Select value={filters.state || "all"} onValueChange={(value) => setFilters({ ...filters, state: value === "all" ? "" : value })}>
                      <SelectTrigger id="filter_state">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {getStatesForCountry(filters.country).map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_state"
                      placeholder="Search by state..."
                      value={filters.state}
                      onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                      disabled={!filters.country}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter_time_zone" className="text-xs">Time Zone</Label>
                  <Select value={filters.time_zone || "all"} onValueChange={(value) => setFilters({ ...filters, time_zone: value === "all" ? "" : value })}>
                    <SelectTrigger id="filter_time_zone">
                      <SelectValue placeholder="All Time Zones" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time Zones</SelectItem>
                      <SelectItem value="EST">EST</SelectItem>
                      <SelectItem value="CST">CST</SelectItem>
                      <SelectItem value="MST">MST</SelectItem>
                      <SelectItem value="PST">PST</SelectItem>
                      <SelectItem value="AST">AST</SelectItem>
                      <SelectItem value="HST">HST</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-md3-on-surface-variant">
                  Showing {filteredLeads.length} leads
                </p>
                <div className="flex gap-2 items-center">
                  {/* Team Member Filter - Only for Team Leads and Managers */}
                  {(isSalesTeamLead || isSalesManager) && teamMembers.length > 0 && (
                    <Select
                      value={selectedTeamMember}
                      onValueChange={(value) => {
                        setSelectedTeamMember(value);
                        // Reset "My Leads" filter when selecting a team member
                        if (value !== "all") {
                          setFilters({ ...filters, show_my_leads: false });
                        }
                      }}
                    >
                      <SelectTrigger className="w-[180px] h-8 text-sm">
                        <SelectValue placeholder="Filter by Team Member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Team Members</SelectItem>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button 
                    variant={filters.show_my_leads ? "default" : "outline"} 
                    size="sm" 
                    onClick={() => {
                      setFilters({ ...filters, show_my_leads: !filters.show_my_leads });
                      // Reset team member filter when clicking "My Leads"
                      if (!filters.show_my_leads) {
                        setSelectedTeamMember("all");
                      }
                    }}
                  >
                    My Leads
                  </Button>
                  <Button variant={filters.no_call_30 ? "default" : "outline"} size="sm" onClick={() => setFilters({ ...filters, no_call_30: !filters.no_call_30 })} data-testid="filter-no-call-30">
                    No call in 30 days
                  </Button>
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"  onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>

            {loading ? (
              <p className="text-md3-on-surface-variant">Loading...</p>
            ) : leads.length === 0 ? (
              <div className="text-center py-12">
                <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No leads yet. Create your first client!</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12">
                <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No leads match your filters.</p>
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Bulk Actions Bar */}
                {selectedLeads.size > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md3-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">
                        {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2" size="sm"
                         onClick={() => setSelectedLeads(new Set())}>
                        Clear Selection
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleOpenBulkSalesAssignment}
                        className="bg-md3-primary hover:bg-md3-primary/90 shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                      >
                        <Users className="mr-2 h-4 w-4" />
                        Assign Sales User
                      </Button>
                    </div>
                  </div>
                )}

                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedLeads.size > 0 && selectedLeads.size === filteredLeads.length}
                          onCheckedChange={handleSelectAllLeads}
                        />
                      </TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Assigned Sales</TableHead>
                      <TableHead>Time Zone</TableHead>
                      <TableHead>Last Call</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow key={lead.id} className={isStaleCall(lead.last_call_at) ? "bg-red-50 border-l-4 border-l-red-400" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={selectedLeads.has(lead.id)}
                            onCheckedChange={() => handleSelectLead(lead.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{getLeadDisplay(lead)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Badge variant="outline">{lead.lead_status || "N/A"}</Badge>
                            {leadApprovals[lead.id]?.approval_status === "approved" && (
                              <Badge className="bg-green-100 text-green-800">Approved</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{lead.industry || "N/A"}</TableCell>
                        <TableCell>
                          {leadSalesUsers[lead.id] ? (
                            <span className="text-md3-body-small">{leadSalesUsers[lead.id].sales_user_name}</span>
                          ) : (
                            <span className="text-xs text-gray-400">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>{lead.time_zone || "EST"}</TableCell>
                        <TableCell data-testid={`lead-last-call-${lead.id}`}><LastCallCell iso={lead.last_call_at} /></TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <LogCallButton entityType="lead" entityId={lead.id} name={lead.lead_name} onLogged={(log) => setLeads((ls) => withLastCall(ls, log))} />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewLead(lead)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewContacts(lead)}
                              title="View Contacts"
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                            {/* Edit button based on permissions */}
                            {user?.permissions?.leads?.edit && leadApprovals[lead.id]?.approval_status !== "approved" && (
                              <Button size="sm"
                                variant="outline"
                                className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                onClick={() => handleEdit(lead)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Delete button based on permissions */}
                            {user?.permissions?.leads?.delete && leadApprovals[lead.id]?.approval_status !== "approved" && (
                              <Button size="sm"
                                variant="destructive"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                onClick={() => handleDelete(lead.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Show info message for approved leads */}
                            {leadApprovals[lead.id]?.approval_status === "approved" && (
                              <span className="text-xs text-md3-on-surface-variant italic">Read-only (Approved)</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Lead Details Dialog */}
        <Dialog open={viewLeadDialogOpen} onOpenChange={setViewLeadDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Lead Details</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">Complete information for {viewingLead?.lead_name}</DialogDescription>
            </DialogHeader>
            {viewingLead && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    <div>
                      <p className="text-md3-on-surface-variant">Lead Number</p>
                      <p className="font-semibold">{viewingLead.lead_number}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Lead Name</p>
                      <p className="font-semibold">{viewingLead.lead_name}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Lead Status</p>
                      <p className="font-semibold">{viewingLead.lead_status}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Industry</p>
                      <p className="font-semibold">{viewingLead.industry}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Software</p>
                      <p className="font-semibold">{viewingLead.software?.join(", ") || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Business Relation Start Date</p>
                      <p className="font-semibold">{viewingLead.business_relation_start_date || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    <div>
                      <p className="text-md3-on-surface-variant">Phone</p>
                      <p className="font-semibold">{viewingLead.phone || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Fax</p>
                      <p className="font-semibold">{viewingLead.fax || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Website</p>
                      <p className="font-semibold">{viewingLead.website || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">Time Zone</p>
                      <p className="font-semibold">{viewingLead.time_zone}</p>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Location</h3>
                  <div className="grid grid-cols-2 gap-4 text-md3-body-small">
                    <div>
                      <p className="text-md3-on-surface-variant">Country</p>
                      <p className="font-semibold">{viewingLead.country || "-"}</p>
                    </div>
                    <div>
                      <p className="text-md3-on-surface-variant">State/Province</p>
                      <p className="font-semibold">{viewingLead.state || "-"}</p>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-md3-title-large mb-3 text-blue-900">Address Information</h3>
                  <div className="text-md3-body-small">
                    <p className="text-md3-on-surface-variant mb-1">Address</p>
                    <p className="font-semibold">
                      {viewingLead.address_line1 && <>{viewingLead.address_line1}<br /></>}
                      {viewingLead.address_line2 && <>{viewingLead.address_line2}<br /></>}
                      {viewingLead.city && <>{viewingLead.city}, </>}
                      {viewingLead.state && <>{viewingLead.state} </>}
                      {viewingLead.zip_code}
                      {viewingLead.country && <><br />{viewingLead.country}</>}
                    </p>
                  </div>
                </div>

                {/* Sales Assignment Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md3-title-large text-blue-900">Sales Assignment</h3>
                    {(user?.department === "Management" || user?.department === "Admin") && (
                      <Button size="sm" 
                        className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleOpenSalesAssignment(viewingLead)}>
                        {leadSalesUsers[viewingLead?.id] ? "Reassign Sales User" : "Assign Sales User"}
                      </Button>
                    )}
                  </div>
                  <div className="text-md3-body-small">
                    <div className="mb-2">
                      <p className="text-md3-on-surface-variant">Current Sales User</p>
                      <p className="font-semibold">
                        {leadSalesUsers[viewingLead?.id]?.sales_user_name || (
                          <span className="text-gray-400">No sales user assigned</span>
                        )}
                      </p>
                      {leadSalesUsers[viewingLead?.id] && (
                        <p className="text-xs text-md3-on-surface-variant mt-1">
                          Since: {leadSalesUsers[viewingLead?.id]?.start_date}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Approval Section - Only show if status is RFQ and user has permission */}
                {viewingLead?.lead_status === "RFQ" && (user?.access_level === "Manager" || user?.access_level === "Management" || user?.access_level === "Admin") && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md3-lg p-4">
                    <h3 className="text-md3-title-large mb-2 text-blue-900">Lead Approval</h3>
                    {leadApprovals[viewingLead?.id] ? (
                      <div>
                        <p className="text-md3-body-small text-md3-on-surface-variant mb-2">
                          This lead has been submitted for approval.
                        </p>
                        <Badge className={
                          leadApprovals[viewingLead?.id].approval_status === "pending" ? "bg-yellow-100 text-yellow-800" :
                          leadApprovals[viewingLead?.id].approval_status === "approved" ? "bg-green-100 text-green-800" :
                          "bg-md3-surface-container text-md3-on-surface"
                        }>
                          Status: {leadApprovals[viewingLead?.id].approval_status}
                        </Badge>
                      </div>
                    ) : (
                      <div>
                        <p className="text-md3-body-small text-md3-on-surface-variant mb-3">
                          This lead is ready for approval. Request approval to convert it to a client.
                        </p>
                        <Button size="sm" 
                          className="bg-md3-primary hover:bg-md3-primary/90 shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full text-md3-on-primary"  onClick={() => handleRequestApproval(viewingLead)}>
                          Request Approval
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <CallHistory entityType="lead" entityId={viewingLead.id} name={viewingLead.lead_name} onLogged={(log) => setLeads((ls) => withLastCall(ls, log))} />

                <div className="flex justify-end pt-4 border-t">
                  <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setViewLeadDialogOpen(false)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sales Assignment Dialog */}
        <Dialog open={salesAssignmentDialogOpen} onOpenChange={setSalesAssignmentDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Assign Sales User</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Assign a sales user to {salesAssignmentLead?.lead_name}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateAssignment} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="sales_user_id" className="text-md3-label-large text-gray-700 font-medium">Sales User *</Label>
                <Select 
                  value={assignmentFormData.sales_user_id} 
                  onValueChange={(value) => setAssignmentFormData({ ...assignmentFormData, sales_user_id: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select sales user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .filter(u => u.department === "Sales" || u.department === "Management")
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.department})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="start_date"
                  type="date"
                  value={assignmentFormData.start_date}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, start_date: e.target.value })}
                />
                <p className="text-xs text-md3-on-surface-variant">
                  Previous assignment will end automatically on {assignmentFormData.start_date ? 
                    new Date(new Date(assignmentFormData.start_date).getTime() - 86400000).toISOString().split('T')[0] : 
                    'the day before'
                  }
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-md3-label-large text-gray-700 font-medium">Notes (Optional)</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="notes"
                  placeholder="Reason for assignment or any notes..."
                  value={assignmentFormData.notes}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Assignment History */}
              {salesAssignmentHistory.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Assignment History</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {[...salesAssignmentHistory]
                      .sort((a, b) => {
                        // Sort by start_date in descending order (latest first)
                        const dateA = new Date(a.start_date);
                        const dateB = new Date(b.start_date);
                        return dateB - dateA;
                      })
                      .map((assignment) => {
                        const assignedUser = users.find(u => u.id === assignment.sales_user_id);
                        return (
                          <div key={assignment.id} className="p-3 bg-md3-surface rounded text-md3-body-small">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">
                                  {assignedUser?.first_name} {assignedUser?.last_name}
                                </p>
                                <p className="text-xs text-md3-on-surface-variant">
                                  {assignment.start_date} → {assignment.end_date || "Present"}
                                </p>
                                {assignment.notes && (
                                  <p className="text-xs text-md3-on-surface-variant mt-1">{assignment.notes}</p>
                                )}
                              </div>
                              {assignment.is_active && (user?.department === "Management" || user?.department === "Admin") && (
                                <Button type="button"
                                  size="sm"
                                  variant="outline"
                                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleEndAssignment(assignment.id, salesAssignmentLead.id)}>
                                  End Now
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setSalesAssignmentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  Assign Sales User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Errors Dialog */}
        <Dialog open={uploadErrorsDialogOpen} onOpenChange={setUploadErrorsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Bulk Upload Results</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {uploadSuccessCount > 0 && (
                  <span className="text-green-600 font-semibold">
                    ✓ Successfully imported {uploadSuccessCount} records
                  </span>
                )}
                {uploadErrors.length > 0 && (
                  <span className="text-red-600 font-semibold block mt-1">
                    ✗ {uploadErrors.length} errors found
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {uploadErrors.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-red-600">Errors:</h4>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {uploadErrors.map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-md3-body-small">
                      <p className="font-medium text-red-800">{error}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end pt-4 border-t">
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setUploadErrorsDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Sales Assignment Dialog */}
        <Dialog open={bulkSalesAssignmentDialogOpen} onOpenChange={setBulkSalesAssignmentDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Bulk Assign Sales User</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Assign a sales user to {selectedLeads.size} selected lead{selectedLeads.size > 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleBulkSalesAssignment} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="bulk_sales_user" className="text-md3-label-large text-gray-700 font-medium">Sales User *</Label>
                <Select
                  value={bulkAssignmentFormData.sales_user_id}
                  onValueChange={(value) => setBulkAssignmentFormData({ ...bulkAssignmentFormData, sales_user_id: value })}
                  required
                >
                  <SelectTrigger id="bulk_sales_user">
                    <SelectValue placeholder="Select sales user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .filter(u => u.department === "Sales")
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk_start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                <DatePicker
                  id="bulk_start_date"
                  value={bulkAssignmentFormData.start_date}
                  onChange={(e) => setBulkAssignmentFormData({ ...bulkAssignmentFormData, start_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk_notes" className="text-md3-label-large text-gray-700 font-medium">Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="bulk_notes"
                  placeholder="Assignment notes..."
                  value={bulkAssignmentFormData.notes}
                  onChange={(e) => setBulkAssignmentFormData({ ...bulkAssignmentFormData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  Assign to {selectedLeads.size} Lead{selectedLeads.size > 1 ? 's' : ''}
                </Button>
                <Button type="button"
                  variant="outline"
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setBulkSalesAssignmentDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Preview Dialog */}
        <BulkImportPreview
          open={importPreviewOpen}
          onClose={() => setImportPreviewOpen(false)}
          previewData={importPreviewData}
          onApply={handleApplyImport}
          entityType="Leads"
        />
      </div>
    </Layout>
  );
}
